// Marketplace account-membership API client (ACL P6, marketplace-account-acl
// design §2/§3.2/§3.3 — the customer /me surface shipped on ecom v111).
//
// Wire shapes mirror the Go handlers exactly (project rule §39):
//   handler: internal/marketplace/handler/customer_handler.go
//     - HandleMeSelfView          (GET  /me/self-view)                    §3.3
//     - HandleMeMembersList       (GET  /me/members)                      §2.4
//     - HandleMeMemberRemove      (POST /me/members/{user_id}/remove)     §2.4
//     - HandleMeMembershipLeave   (POST /me/membership/leave)             §3.2
//     - HandleMeInvitationsList   (GET  /me/member-invitations)           §2.5
//     - HandleMeInvitationCreate  (POST /me/member-invitations)           §2.2
//     - HandleMeInvitationRevoke  (POST /me/member-invitations/{id}/revoke)
//     - HandleMeInvitationResend  (POST /me/member-invitations/{id}/resend)
//     - HandleMeMembershipAccept  (POST /me/membership/accept)            §3.2
//     - HandleMeMembershipTransfer(POST /me/membership/transfer)          §RULING T-1
//   router: internal/marketplace/handler/customer_router.go:320-374
//
// DIVERGENCE from the P6 plan (handler wins — plan §0's rule), RESOLVED in
// the post-lane review: the plan's §1.2 sketch showed "name/email + role",
// but the members wire as P2 shipped it carried NO user label — no join on
// the route, no customer-side bulk user lookup to enrich it client-side.
// The lane rendered "Member #<user_id>" and reported the gap; the review
// added the backend LEFT JOIN (email on the LIST projection only — see
// MembershipWire), because the ownership-transfer picker choosing between
// "Member #42" and "Member #43" is exactly the mis-association rule 66
// exists to prevent.
//
// All handlers emit `{"data": <payload>}` — after embedApiFetch's .data
// unwrap every function here returns the payload directly (the referral-api
// convention). No If-Match anywhere: none of these routes is
// optimistic-locked (the lifecycle rows' terminal states ARE the idempotency
// — see the router's per-route rule-79 notes), so there is no version to
// carry.

import { embedApiFetch } from "../../embed-shared/api-client";

// ─── ACL area keys (presentation gating) ────────────────────────────────────
//
// The 15-area catalog, Go model.ACLAreaKeys verbatim (acl.go §3.1 — migration
// 128's seed plus migration 150's view-products is the source of truth). Only the areas the embed actually gates
// on are mirrored here; §55 discipline — consumers import the constant, never
// re-string it. self-view's effective_permissions is keyed by these strings.

/** Owner-transferable member administration (the "Members" section: list, invites, transfer). */
export const ACL_AREA_MANAGE_MEMBERSHIP = "manage-membership";
/**
 * "Profile details" form within the (ungated) "Profile & security"
 * section's Profile TAB — company/contact fields on /me/account
 * (ProfilePanel.tsx's `<section aria-label="Profile details">`).
 * ⚠️ AMENDED 2026-10-04 (account-area redesign Task 8). ORIGINAL: "the
 * sub-sections of the ungated Profile section" (Addresses and Security
 * described as sibling sub-sections of the same stacked block). CURRENT:
 * ProfilePanel renders kinds of information as TABS — this area gates the
 * editable form on the Profile tab, NOT the Profile tab itself, and the
 * read-only identity rows render ungated, so a member denied this area
 * still reaches Profile (rows + Leave) and the ungated Sign-in & security
 * tab.
 */
export const ACL_AREA_MANAGE_ACCOUNT_SETTINGS = "manage-account-settings";
/**
 * Addresses TAB of the (ungated) "Profile & security" section (/me/
 * addresses CRUD). ⚠️ AMENDED 2026-10-04 (account-area redesign Task 8):
 * was "Addresses sub-section within the ungated Profile section" — the
 * amendment makes it a TAB, so the TAB ITSELF hides when this is denied
 * (the deny-shortcut can no longer even open the list).
 */
export const ACL_AREA_MANAGE_ADDRESSES = "manage-addresses";
/** "Orders" section (/me/orders reads). */
export const ACL_AREA_VIEW_ORDERS = "view-orders";
/**
 * "Stores" section (store records and the assignment of hardware units to them).
 * ⚠️ AMENDED 2026-10-10 (merge-review M2, TD-583). ORIGINAL: also gated the
 * "Products & subscriptions" section. CURRENT: Products has its own area,
 * ACL_AREA_VIEW_PRODUCTS; this one gates Stores (and the Move units action
 * INSIDE Products, since it reads /me/stores and posts a move).
 */
export const ACL_AREA_MANAGE_STORES = "manage-stores";
/**
 * "Products & subscriptions" section. Mirrors `model.AreaViewProducts`
 * (migration 150). Operator answer 2026-10-10: "New products permission" --
 * "A dedicated ACL area. Needs a seed migration and a column in the portal
 * permission matrix." A PRESENTATION gate: it decides whether the section is
 * offered; the data inside is still governed by view-orders,
 * manage-subscriptions, manage-stores and manage-rma, and each panel degrades
 * on its own area (see `productsAccess`).
 */
export const ACL_AREA_VIEW_PRODUCTS = "view-products";
/**
 * Returns / RMA requests. Mirrors `model.AreaManageRMA`. Governs the "Request
 * a return" action and the return Status inside the Products section.
 */
export const ACL_AREA_MANAGE_RMA = "manage-rma";
/** "Merchant services" section (the merchant accounts linked to your stores). */
export const ACL_AREA_MANAGE_MIDS = "manage-mids";
/** "Refer & Save" section. */
export const ACL_AREA_PARTICIPATE_REFERRALS = "participate-referrals";
/**
 * Subscription management. Mirrors `model.AreaManageSubscriptions` in
 * `internal/marketplace/model/acl.go`. No SECTION is gated on it: the Products
 * section is gated on view-products (TD-583), and the Subscriptions tab inside
 * it degrades on this area (`productsAccess`).
 */
export const ACL_AREA_MANAGE_SUBSCRIPTIONS = "manage-subscriptions";
/**
 * "Notifications" section (it was a sub-section of the Welcome landing before
 * the account-area redesign) — gates the mount alongside `selfViewSettled` (fail-open on an unsettled self-view
 * would otherwise dispatch a fetch before the ACL answer arrives).
 */
export const ACL_AREA_READ_NOTIFICATIONS = "read-notifications";

/**
 * The union of every ACL area key mirrored above (TD-472). Derived via
 * `typeof` off the constants themselves (rule 55 — never a hardcoded
 * duplicate of their string values), so a section-registry entry or an
 * `isAreaPermitted` call that gates on a typo'd key (e.g. "view-order" for
 * "view-orders") now fails to compile instead of matching no key in
 * `effective_permissions` and silently defaulting to visible.
 */
export type AclArea =
  | typeof ACL_AREA_MANAGE_MEMBERSHIP
  | typeof ACL_AREA_MANAGE_ACCOUNT_SETTINGS
  | typeof ACL_AREA_MANAGE_ADDRESSES
  | typeof ACL_AREA_VIEW_ORDERS
  | typeof ACL_AREA_MANAGE_STORES
  | typeof ACL_AREA_VIEW_PRODUCTS
  | typeof ACL_AREA_MANAGE_RMA
  | typeof ACL_AREA_MANAGE_MIDS
  | typeof ACL_AREA_PARTICIPATE_REFERRALS
  | typeof ACL_AREA_MANAGE_SUBSCRIPTIONS
  | typeof ACL_AREA_READ_NOTIFICATIONS;

// ─── Wire shapes (§39 — derived from the handler response structs) ──────────

/**
 * One live membership row — the minimal public projection the handlers emit
 * (membershipResponse). `invited_by_user_id` has NO omitempty in Go
 * (*int64, always marshaled): it is `number | null`, never absent. NULL for
 * owners (not invited). joined_at is the application-level join moment
 * (rule 9 keeps the DB-internal timestamps off the wire), RFC3339.
 *
 * `email` rides ONLY the members-LIST projection (Go omitempty): the list's
 * LEFT JOIN resolves the member's login identity — the human label rule 66
 * wants in the transfer picker. '' / absent is the soft-deleted-user shape
 * (the join keeps the row, the label falls back to the id). Single-row
 * lifecycle responses (accept/leave/remove/transfer) never carry it.
 */
export interface MembershipWire {
  id: number;
  user_id: number;
  email?: string;
  role: string; // owner | manager | user | guest (DDL check)
  joined_at: string; // RFC3339
  invited_by_user_id: number | null;
}

/**
 * The acting account half of self-view (selfViewAccountResponse). Always
 * present on a 200 — the handler constructs it unconditionally (§3.2: the
 * identity half of self-view always answers). company_name is nullable
 * (Go *string, rule 2) — `string | null`, never absent.
 */
export interface SelfViewAccountWire {
  id: number; // marketplace_customers id
  customer_type: string; // buyer | reseller | prospect
  company_name: string | null;
}

/**
 * GET /me/self-view's payload (selfViewResponse) — the §3.3 "matrix delivery
 * to the UI" read. role is "owner"|"manager"|"user"|"guest", or "" for the
 * legacy non-owner state (§2.6(b)); the empty string is surfaced VERBATIM so
 * the UI renders the same nothing-allowed state the server enforces.
 * effective_permissions carries all 15 area keys, each server-derived (owner
 * ⇒ implicit-all; absent cell ⇒ false — fail-closed delivery). membership is
 * the caller's live row, or null for legacy shapes that predate membership
 * rows (solo owners; the "" role).
 *
 * The map covers ONLY the 15 matrix-governed areas: the §3.2 always-allowed
 * surface (passkeys, self-leave, accept, catalog, identity read) is outside
 * the map BY DESIGN — never read an absence as "denied" for those.
 */
export interface SelfViewWire {
  account: SelfViewAccountWire;
  role: string;
  effective_permissions: Record<string, boolean>;
  membership: MembershipWire | null;
}

/**
 * One PENDING invitation (invitationResponse) — deliberately minimal: the
 * token hash is structurally absent (the raw token exists only in the
 * invitee's email). revoked_at is omitempty in Go: present ONLY on
 * revoke/resend RESPONSES (the list is pending-only); typed optional.
 */
export interface InvitationWire {
  id: string; // UUID
  invitee_email: string;
  offered_role: string; // manager | user | guest (DDL check — owner is not invitable)
  expires_at: string; // RFC3339
  invited_by_user_id: number | null;
  revoked_at?: string; // RFC3339 — revoke/resend responses only
}

/** POST /me/membership/accept's payload: the minted membership plus, ONLY
 *  when the accepter's money-carrying prospect was retained (A-P2-1), the
 *  notice telling them (§2.6 — present iff non-empty server-side). */
export interface AcceptInvitationResultWire {
  membership: MembershipWire;
  prospect_retained_notice?: string;
}

/** POST /me/membership/transfer's payload: the re-pointed account projection
 *  (owner back-reference + bumped version) and both affected memberships.
 *  Full customer fields stay off this wire — the SPA re-reads /me/account. */
export interface TransferOwnershipResultWire {
  account: {
    id: number;
    owner_user_id: number | null;
    version: number;
  };
  old_owner: MembershipWire;
  successor: MembershipWire;
}

/** The offered-role choices an invitation may carry (DDL
 *  ck_account_invitations__offered_role). "owner" is NOT invitable — the
 *  server rejects it with the 400 VALIDATION_ERROR message the panel
 *  surfaces verbatim. */
export type OfferedRole = "manager" | "user" | "guest";

const SELF_VIEW_PATH = "/api/v1/marketplace/me/self-view";
const MEMBERS_PATH = "/api/v1/marketplace/me/members";
const MEMBERSHIP_PATH = "/api/v1/marketplace/me/membership";
const INVITATIONS_PATH = "/api/v1/marketplace/me/member-invitations";

// ─── Self-view (§3.3) ───────────────────────────────────────────────────────

/**
 * Read the caller's acting role + server-derived effective permissions.
 *
 * GET /me/self-view → SelfViewWire after the .data unwrap. Resolves null
 * when the envelope carries no data (embedApiFetch's null unwrap) — callers
 * treat null as "no self-view" (presentation keeps pre-ACL visibility; the
 * server refusals remain the control either way).
 *
 * §3.2 always-allowed: a GUEST reads this; that is the point. A member must
 * see which account they act in to render any UI at all.
 */
export async function getSelfView(): Promise<SelfViewWire | null> {
  const result = await embedApiFetch<SelfViewWire>(SELF_VIEW_PATH, { query: {} });
  return result ?? null;
}

// ─── Members + self-leave (§2.4/§3.2) ───────────────────────────────────────

/**
 * List the acting account's LIVE memberships. GET /me/members →
 * MembershipWire[]; the handler always emits an array (empty ⇒ data:[]), the
 * `?? []` guards only a degenerate null unwrap.
 *
 * Throws ApiError(403-ish) when the caller's role lacks manage-membership —
 * the server is the control; the UI gates presentation on self-view.
 */
export async function listMembers(): Promise<MembershipWire[]> {
  const result = await embedApiFetch<MembershipWire[]>(MEMBERS_PATH, { query: {} });
  return result ?? [];
}

/**
 * Remove a member from the acting account. POST /me/members/{user_id}/remove
 * → the removed membership row. The user_id is the WIRE value (rule 66: the
 * UI picks a human label, this id is what submits).
 *
 * Throws ApiError(403, OWNER_REQUIRED, "The account owner cannot leave or be
 * removed — transfer ownership first") when the target is the owner;
 * ApiError(404, NOT_FOUND, "Member not found") for an unknown/foreign target
 * (deliberately indistinguishable). Those messages are the contract — the
 * panel surfaces them VERBATIM.
 */
export async function removeMember(userId: number): Promise<MembershipWire> {
  return embedApiFetch<MembershipWire>(`${MEMBERS_PATH}/${userId}/remove`, { method: "POST" });
}

/**
 * Leave the acting account (§3.2 always-allowed — a member can never be
 * trapped). POST /me/membership/leave → the removed membership row. No body.
 *
 * Throws ApiError(403, OWNER_REQUIRED, "The account owner cannot leave or be
 * removed — transfer ownership first") — the last-owner refusal, surfaced
 * verbatim by the UI; ApiError(404, "No live membership to leave").
 */
export async function leaveMembership(): Promise<MembershipWire> {
  return embedApiFetch<MembershipWire>(`${MEMBERSHIP_PATH}/leave`, { method: "POST" });
}

// ─── Invitations (§2.2/§2.5) ────────────────────────────────────────────────

/**
 * List the acting account's PENDING invitations (expired-but-unrevoked
 * included — pending is the partial-unique-index predicate). GET
 * /me/member-invitations → InvitationWire[].
 */
export async function listInvitations(): Promise<InvitationWire[]> {
  const result = await embedApiFetch<InvitationWire[]>(INVITATIONS_PATH, { query: {} });
  return result ?? [];
}

/**
 * Invite a person by email with an offered role. POST /me/member-invitations
 * {email, role} → the created InvitationWire (the raw token is NEVER on this
 * wire — it exists in the invitee's email only).
 *
 * The §2.5 exclusivity refusals are the UI's contract, surfaced VERBATIM:
 *   409 INVITEE_ALREADY_MEMBER — "That person is already a member of this
 *                                 account" (the acting account only — TD-714)
 *   409 INVITATION_CONFLICT    — "A pending invitation for that email already
 *                                 exists — resend or revoke it first"
 *   400 VALIDATION_ERROR       — "A valid email address and a role of
 *                                 manager, user or guest are required"
 */
export async function createInvitation(email: string, role: OfferedRole): Promise<InvitationWire> {
  return embedApiFetch<InvitationWire>(INVITATIONS_PATH, {
    method: "POST",
    body: { email: email.trim(), role },
  });
}

/**
 * Revoke a pending invitation. POST /me/member-invitations/{id}/revoke → the
 * row's projection with revoked_at set. Idempotent by design (an
 * already-revoked/consumed row is a no-op 200); 404 "Invitation not found"
 * for unknown/foreign ids.
 */
export async function revokeInvitation(id: string): Promise<InvitationWire> {
  return embedApiFetch<InvitationWire>(`${INVITATIONS_PATH}/${id}/revoke`, { method: "POST" });
}

/**
 * Resend a pending invitation (revoke + recreate: a FRESH row with a new
 * token + expiry). POST /me/member-invitations/{id}/resend → the fresh row's
 * projection. 404 "No such pending invitation" for unknown/foreign/already-
 * consumed ids — the caller's own revoke made the old row non-pending, so a
 * retried resend gets the same 404.
 */
export async function resendInvitation(id: string): Promise<InvitationWire> {
  return embedApiFetch<InvitationWire>(`${INVITATIONS_PATH}/${id}/resend`, { method: "POST" });
}

// ─── Accept + transfer (§3.2, §RULING T-1) ──────────────────────────────────

/**
 * Redeem an invitation token. POST /me/membership/accept {token} → the minted
 * membership (+ prospect_retained_notice iff the accepter's money-carrying
 * prospect was retained).
 *
 * §3.2 always-allowed: the accepter is pre-membership by definition — this
 * route never consults the matrix and the accepter may hold NO resolvable
 * account, so it fires for any authenticated user.
 *
 * Refusals (surfaced VERBATIM — they are the flow's outcomes, not errors to
 * reinterpret): 404 NOT_FOUND "This invitation is no longer valid" (unknown/
 * expired/revoked/consumed alike — anti-enumeration names nothing); 409
 * ALREADY_MEMBER "You are already a member of an account — leave it before
 * joining another".
 */
export async function acceptInvitation(token: string): Promise<AcceptInvitationResultWire> {
  return embedApiFetch<AcceptInvitationResultWire>(`${MEMBERSHIP_PATH}/accept`, {
    method: "POST",
    body: { token },
  });
}

/**
 * Transfer account ownership to a LIVE-MEMBER successor (§RULING T-1).
 * POST /me/membership/transfer {successor_user_id} → the post-commit
 * projection. The id is the WIRE value (rule 66: the UI is a label picker
 * over live members, never a raw-id input).
 *
 * Owner-only, matrix-INDEPENDENT (P7). Refusals surfaced verbatim: 403
 * OWNER_REQUIRED "Only the account owner can transfer ownership"; 409
 * ALREADY_OWNER "That member already owns this account"; 404 "Successor is
 * not a live member of this account"; 409 VERSION_MISMATCH "The account
 * changed concurrently — retry".
 *
 * After a 200 the CALLER'S ROLE HAS CHANGED (owner → manager): the caller
 * MUST re-read self-view rather than reuse the stale one (plan §1.5).
 */
export async function transferOwnership(successorUserId: number): Promise<TransferOwnershipResultWire> {
  return embedApiFetch<TransferOwnershipResultWire>(`${MEMBERSHIP_PATH}/transfer`, {
    method: "POST",
    body: { successor_user_id: successorUserId },
  });
}
